<template>
  <div class="kv-message-wrapper kv-scrollbar" :style="computedStyle" :class="[typeClass]">
    <div class="kv-message-inner">

      <div class="kv-time" v-if="type !== 'rcs' && !isWhatsappCloudTemplate(options)">
        <HumanDate v-if="type == 'sms'" :date="time" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
        <HumanDate v-else :date="time" format="[h]:[m]" />
      </div>

      <KvSkeleton v-if="isLoadingTemplate" class="kv-message-renderer kv-skeleton-wrapper" preset="message" :bg-base="type === 'sms' ? '#d1d5db' : '#f0f2f5'" :bg-highlight="type === 'sms' ? '#e5e7eb' : '#e6e8eb'" />

      <div v-else-if="renderedTemplateHtml" v-html="renderedTemplateHtml" class="kv-message-renderer kv-wpp-template-container">
      </div>

      <div v-else-if="type === 'rcs' && rcsView" class="kv-message-renderer kv-rcs">
        <component :is="rcsView.fileUrl ? 'a' : 'div'" v-if="rcsView.kind === 'file'" class="kv-rcs-file" :href="rcsView.fileUrl || undefined" :target="rcsView.fileUrl ? '_blank' : undefined" :rel="rcsView.fileUrl ? 'noopener' : undefined">
          <span class="kv-rcs-file-icon"><i class="fa fa-file-text-o"></i></span>
          <span class="kv-rcs-file-meta">
            <span class="kv-rcs-file-name">{{ rcsView.fileName }}</span>
            <span class="kv-rcs-file-kind">{{ rcsView.fileKind }}</span>
          </span>
        </component>

        <div v-else-if="rcsView.kind === 'carousel'" class="kv-rcs-carousel">
          <article v-for="(card, index) in rcsView.cards" :key="index" class="kv-rcs-card">
            <div class="kv-rcs-media" :class="{ 'is-empty': !card.image || rcsBrokenImages[card.image] }">
              <img v-if="card.image && !rcsBrokenImages[card.image]" :src="card.image" alt="" @error="markRcsImageBroken(card.image)" />
              <i v-else class="fa fa-picture-o"></i>
            </div>
            <div class="kv-rcs-card-body">
              <div v-if="card.title" class="kv-rcs-title">{{ card.title }}</div>
              <div v-if="card.messageHtml" class="kv-rcs-text" v-html="card.messageHtml"></div>
            </div>
            <div v-if="card.buttons.length" class="kv-rcs-actions">
              <component
                :is="button.url ? 'a' : 'span'"
                v-for="(button, buttonIndex) in card.buttons"
                :key="buttonIndex"
                class="kv-rcs-action"
                :href="button.url || undefined"
                :target="button.url ? '_blank' : undefined"
                :rel="button.url ? 'noopener' : undefined"
              >
                <i class="fa" :class="button.url ? 'fa-external-link' : 'fa-reply'"></i>
                <span>{{ button.text }}</span>
              </component>
            </div>
          </article>
        </div>

        <article v-else-if="rcsView.kind === 'card'" class="kv-rcs-card is-single">
          <div class="kv-rcs-media" :class="{ 'is-empty': !rcsView.card.image || rcsBrokenImages[rcsView.card.image] }">
            <img v-if="rcsView.card.image && !rcsBrokenImages[rcsView.card.image]" :src="rcsView.card.image" alt="" @error="markRcsImageBroken(rcsView.card.image)" />
            <i v-else class="fa fa-picture-o"></i>
          </div>
          <div class="kv-rcs-card-body">
            <div v-if="rcsView.card.title" class="kv-rcs-title">{{ rcsView.card.title }}</div>
            <div v-if="rcsView.card.messageHtml" class="kv-rcs-text" v-html="rcsView.card.messageHtml"></div>
          </div>
          <div v-if="rcsView.card.buttons.length" class="kv-rcs-actions">
            <component
              :is="button.url ? 'a' : 'span'"
              v-for="(button, buttonIndex) in rcsView.card.buttons"
              :key="buttonIndex"
              class="kv-rcs-action"
              :href="button.url || undefined"
              :target="button.url ? '_blank' : undefined"
              :rel="button.url ? 'noopener' : undefined"
            >
              <i class="fa" :class="button.url ? 'fa-external-link' : 'fa-reply'"></i>
              <span>{{ button.text }}</span>
            </component>
          </div>
        </article>

        <div v-else class="kv-rcs-bubble" v-html="formattedContent" />
      </div>

      <div v-else class="kv-message-renderer" v-html="formattedContent" />

      <div class="kv-time" v-if="type === 'rcs'">
        <HumanDate :date="time" format="[h]:[m]" />
      </div>
    </div>
  </div>
</template>

<script>
import HumanDate from '@/components/human-date/human-date.vue';
import keviApi from '@/keviApi';
import KvSkeleton from '@/components/ui/kv-skeleton.vue';
import { parseMarkdownToHtml } from '@/helpers/textFormatter';

let sharedWppTemplatesPromise = null;
let sharedWppTemplatesCache = {};

export default {
  name: "KvMessageRenderer",
  components: { HumanDate, KvSkeleton },
  data() {
    return {
      wppCloudTemplates: sharedWppTemplatesCache,
      wppCloudSendersLoading: false,
      rcsBrokenImages: {},
    }
  },
  props: {
    content: { type: String },
    height: { type: [String, Number], default: null },
    maxHeight: { type: [String, Number], default: null },
    time: { type: [String, Date] },
    type: { type: String, default: "whatsapp" },
    options: { type: [Object, String], default: null },
    sendersId: { type: [Number, String], default: null },
    templates: { type: [Array, Object], default: null },
  },
  mounted() {
    if (this.isWhatsappCloudTemplate(this.options) && !this.templates) {
      this.loadAllWppCloudTemplatesIfNeeded();
    }
  },
  computed: {
    typeClass() { return `kv-message-${this.type}`; },

    computedStyle() {
      const style = { overflow: "auto" };
      if (this.height) style.height = typeof this.height === "number" ? `${this.height}px` : this.height;
      if (this.maxHeight) style.maxHeight = typeof this.maxHeight === "number" ? `${this.maxHeight}px` : this.maxHeight;
      return style;
    },

    formattedContent() {
      const html = parseMarkdownToHtml(this.content);

      if (this.type === 'whatsapp') {
        return `<div class="wpp-content-wrapper"><div class="wpp-body">${html}</div></div>`;
      }
      return html;
    },

    rcsView() {
      if (this.type !== 'rcs') {
        return null;
      }
      const opts = this.parseRcsOptions(this.options);
      const type = opts.type || 'basic';

      if (type === 'file') {
        const fileName = String(opts.fileName || this.content || 'Arquivo').trim() || 'Arquivo';
        return {
          kind: 'file',
          fileName,
          fileUrl: this.safeHttpUrl(opts.file),
          fileKind: this.rcsFileKind(fileName),
        };
      }

      if (type === 'carousel') {
        const cards = (Array.isArray(opts.cards) ? opts.cards : [])
          .map((card) => this.normalizeRcsCard(card, ''))
          .filter((card) => card.image || card.title || card.messageHtml || card.buttons.length);
        if (cards.length) {
          return { kind: 'carousel', cards };
        }
      }

      if (type === 'card' || opts.cardImage || opts.cardTitle || opts.cardMessage) {
        const card = this.normalizeRcsCard(opts, this.content);
        if (card.image || card.title || card.messageHtml || card.buttons.length) {
          return { kind: 'card', card };
        }
      }

      return { kind: 'text' };
    },

    renderedTemplateHtml() {
      return this.renderWhatsappCloudPreview(this.options);
    },

    isLoadingTemplate() {
      const isTemplate = this.isWhatsappCloudTemplate(this.options);
      if (!isTemplate) return false;
      if (this.templates) return false;
      if (this.wppCloudSendersLoading) return true;
      if (this.renderedTemplateHtml) return false;
      const hasCache = Object.keys(this.wppCloudTemplates).length > 0;
      if (!hasCache) return true;
      return false;
    }
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    safeHttpUrl(value) {
      const url = String(value || '').trim();
      if (/^https?:\/\//i.test(url)) {
        return url;
      }
      return '';
    },
    parseRcsOptions(options) {
      if (!options) {
        return {};
      }
      if (typeof options === 'string') {
        try {
          const parsed = JSON.parse(options);
          return parsed && typeof parsed === 'object' ? parsed : {};
        } catch (e) {
          return {};
        }
      }
      return typeof options === 'object' ? options : {};
    },
    normalizeRcsCard(card, fallbackMessage) {
      const source = card && typeof card === 'object' ? card : {};
      const message = String(source.cardMessage || fallbackMessage || '').trim();
      return {
        image: this.safeHttpUrl(source.cardImage),
        title: String(source.cardTitle || '').trim(),
        messageHtml: message ? parseMarkdownToHtml(message) : '',
        buttons: this.normalizeRcsButtons(source.buttons),
      };
    },
    normalizeRcsButtons(buttons) {
      if (!Array.isArray(buttons)) {
        return [];
      }
      return buttons
        .map((button) => {
          if (!button || typeof button !== 'object') {
            return null;
          }
          const text = String(button.text || '').trim();
          if (!text) {
            return null;
          }
          return { text, url: this.safeHttpUrl(button.url) };
        })
        .filter(Boolean);
    },
    rcsFileKind(fileName) {
      const match = String(fileName || '').match(/\.([a-z0-9]{1,8})$/i);
      return match ? match[1].toUpperCase() : 'Arquivo';
    },
    markRcsImageBroken(src) {
      if (src) {
        this.$set(this.rcsBrokenImages, src, true);
      }
    },
    isWhatsappCloudTemplate(options) {
      if (!options) return false;
      let opts = options;
      if (typeof opts === "string") {
        try { opts = JSON.parse(opts); } catch (e) { return false; }
      }
      return !!opts.template_id;
    },

    formatTimeSimple(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      const h = String(date.getHours()).padStart(2, '0');
      const m = String(date.getMinutes()).padStart(2, '0');
      return `${h}:${m}`;
    },

    renderWhatsappCloudPreview(options) {
      if (!options) return null;
      try {
        let opts = options || {};
        if (typeof opts === "string") {
          try { opts = JSON.parse(opts); } catch (e) { void e; opts = {}; }
        }

        const senderId = this.sendersId;
        if (!opts || !opts.template_id) return null;

        let template = null;

        // Estratégia de busca do template
        if (senderId) {
          template = this.getWppCloudTemplate(senderId, opts.template_id);
        }

        if (!template && Array.isArray(this.templates)) {
          template = this.templates.find(t => String(t.id) === String(opts.template_id));
        }

        if (!template && !this.templates) {
          for (const sid of Object.keys(this.wppCloudTemplates)) {
            const found = this.getWppCloudTemplate(sid, opts.template_id);
            if (found) { template = found; break; }
          }
        }

        // Prepara a hora (usada tanto no sucesso quanto no fallback de erro)
        const timeString = this.formatTimeSimple(this.time);
        const timeHtml = timeString ? `<span class="wpp-embedded-time">${timeString}</span>` : '';

        // --- FALLBACK PARA TEMPLATE NÃO ENCONTRADO ---
        if (!template) {
          return `
            <div class="wpp-content-wrapper">
              <div class="wpp-body wpp-missing-body">
                <div><i class="fa fa-exclamation-triangle"></i> ${this.t('reports-template_unavailable')}</div>
                <span class="wpp-missing-id">ID: ${opts.template_id || '?'}</span>
              </div>
              <div class="wpp-body" style="padding-top: 4px; min-height: 20px;">${timeHtml}</div>
            </div>`;
        }
        // ----------------------------------------------

        const components = template.components || [];

        let contentHtml = '';
        let buttonsHtml = '';
        let timeInjected = false;

        const footerComp = components.find(c => c.type === 'FOOTER');
        const hasFooter = footerComp && footerComp.text;

        // HEADER
        const header = components.find(c => c.type === 'HEADER');
        if (header) {
          if (header.format === 'TEXT' && header.text) {
            let text = parseMarkdownToHtml(header.text);

            // Lógica para pegar parametros do Header (header_text)
            let params = (opts.params && opts.params.header_text) || [];

            // Fallback: header_handle as an array of {param_name, value}
            if ((!params || params.length === 0) && Array.isArray(opts.params?.header_handle)) {
              const namedHandles = opts.params.header_handle.filter(p => p && typeof p === 'object' && p.param_name);
              if (namedHandles.length > 0) {
                params = {};
                for (const p of namedHandles) { params[p.param_name] = p.value; }
              }
            }

            // Fallback para exemplo se não houver params
            if ((!params || params.length === 0) && header.example?.header_text?.[0]) {
              params = header.example.header_text[0];
            }

            // Switch {{1}} (numeric) and {{name}} (named)
            text = text.replace(/\{\{(\w+)\}\}/g, (m, p1) => {
              if (/^\d+$/.test(p1)) {
                const idx = parseInt(p1, 10) - 1;
                return Array.isArray(params) && params[idx] !== undefined && params[idx] !== null ? String(params[idx]) : m;
              } else {
                if (params && typeof params === 'object' && !Array.isArray(params) && params[p1] !== undefined && params[p1] !== null) {
                  return String(params[p1]);
                }
                return m;
              }
            });

            contentHtml += `<div class="wpp-header">${text}</div>`;

          } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.format)) {
            let mediaUrl = null;
            let filename = null; // Usado apenas para documentos

            // 1. Tenta pegar dos parâmetros dinâmicos (opts.params)
            // Ajuste as chaves conforme o objeto que você envia (ex: header_url, header_document, etc)
            if (opts.params) {
              mediaUrl = opts.params.header_url || opts.params.video_url || opts.params.document_url;
              filename = opts.params.filename || 'Arquivo';
            }

            // 2. Fallback: Dados de exemplo do template (API do WhatsApp)
            if (!mediaUrl && header.example && header.example.header_handle && header.example.header_handle[0]) {
              mediaUrl = header.example.header_handle[0];
            }

            // --- RENDERIZAÇÃO ---

            // CASO 1: IMAGEM (Com URL)
            if (header.format === 'IMAGE' && mediaUrl) {
              contentHtml += `
                              <div class="wpp-media-full">
                                <img src="${mediaUrl}" alt="${this.t('image')}" />
                              </div>`;
            }

            // CASO 2: VÍDEO (Com URL)
            else if (header.format === 'VIDEO' && mediaUrl) {
              contentHtml += `
      <div class="wpp-media-full">
        <video controls>
          <source src="${mediaUrl}" type="video/mp4">
          ${this.t('reports-video_tag_not_supported')}
        </video>
      </div>`;
            }

            // CASO 3: DOCUMENTO (Com URL)
            else if (header.format === 'DOCUMENT' && mediaUrl) {
              // Tenta limpar o nome do arquivo se vier da URL e não tiver sido definido
              if (filename === 'Arquivo' || !filename) {
                try { filename = mediaUrl.split('/').pop().split('?')[0] || 'Documento'; } catch (e) { }
              }

              contentHtml += `
      <div class="wpp-media-doc">
        <a href="${mediaUrl}" target="_blank" class="wpp-doc-card">
            <div class="wpp-doc-icon">
                <i class="fa fa-file-text"></i>
            </div>
            <span class="wpp-doc-name">${filename}</span>
            <div class="wpp-doc-dl">
                <i class="fa fa-download"></i>
            </div>
        </a>
      </div>`;
            }

            else {
              const icon = header.format === 'IMAGE' ? 'fa-image' : header.format === 'VIDEO' ? 'fa-video-camera' : 'fa-file-text';
              contentHtml += `<div class="wpp-media wpp-media-placeholder"><i class="fa ${icon}"></i> ${header.format}</div>`;
            }
          }
        }

        // BODY
        const body = components.find(c => c.type === 'BODY');
        if (body && body.text) {
          let text = parseMarkdownToHtml(body.text);
          let params = (opts.params && opts.params.body_text) || [];

          // Fallback: body_handle as an array of {param_name, value}
          if ((!params || params.length === 0) && Array.isArray(opts.params?.body_handle)) {
            const namedHandles = opts.params.body_handle.filter(p => p && typeof p === 'object' && p.param_name);
            if (namedHandles.length > 0) {
              params = {};
              for (const p of namedHandles) { params[p.param_name] = p.value; }
            }
          }

          if ((!params || params.length === 0) && body.example?.body_text?.[0]) {
            params = body.example.body_text[0];
          }
          text = text.replace(/\{\{(\w+)\}\}/g, (m, p1) => {
            if (/^\d+$/.test(p1)) {
              const idx = parseInt(p1, 10) - 1;
              return Array.isArray(params) && params[idx] !== undefined && params[idx] !== null ? String(params[idx]) : m;
            } else {
              if (params && typeof params === 'object' && !Array.isArray(params) && params[p1] !== undefined && params[p1] !== null) {
                return String(params[p1]);
              }
              return m;
            }
          });

          if (!hasFooter) {
            contentHtml += `<div class="wpp-body">${text}${timeHtml}</div>`;
            timeInjected = true;
          } else {
            contentHtml += `<div class="wpp-body">${text}</div>`;
          }
        }

        // FOOTER
        if (hasFooter) {
          contentHtml += `<div class="wpp-footer">${parseMarkdownToHtml(footerComp.text)}${timeHtml}</div>`;
          timeInjected = true;
        }

        if (!timeInjected && timeHtml) {
          contentHtml += `<div class="wpp-body" style="padding-top: 4px; min-height: 20px;">${timeHtml}</div>`;
        }

        // BUTTONS
        const buttonsComp = components.find(c => c.type === 'BUTTONS');
        if (buttonsComp && buttonsComp.buttons && buttonsComp.buttons.length > 0) {
          buttonsHtml += '<div class="wpp-buttons-container">';
          const buttonParams = (opts.params && opts.params.buttons) || [];

          buttonsComp.buttons.forEach((btn, index) => {
            let iconHtml = '';
            let url = '#';
            let classes = 'wpp-btn';

            if (btn.type === 'URL') {
              iconHtml = '<i class="fa fa-external-link"></i>';
              url = btn.url || '#';
              if (url.includes('{{1}}')) {
                const param = buttonParams[index];
                if (param) url = url.replace('{{1}}', param);
              }
              classes += ' wpp-btn-url';

            } else if (btn.type === 'PHONE_NUMBER') {
              iconHtml = '<i class="fa fa-phone"></i>';
              url = `tel:${btn.phone_number}`;
            } else {
              iconHtml = '<i class="fa fa-reply"></i>';
            }

            buttonsHtml += `
              <a href="${url}" target="_blank" class="${classes}">
                <span class="wpp-btn-icon">${iconHtml}</span>
                <span class="wpp-btn-text">${btn.text}</span>
              </a>`;
          });
          buttonsHtml += '</div>';
        }

        return `<div class="wpp-content-wrapper">${contentHtml}</div>${buttonsHtml}`;

      } catch (e) {
        console.error("Failed render", e);
        return null;
      }
    },

    async loadAllWppCloudTemplatesIfNeeded() {
      if (this.templates) return;

      if (Object.keys(sharedWppTemplatesCache).length > 0) {
        this.wppCloudTemplates = sharedWppTemplatesCache;
        return;
      }

      if (sharedWppTemplatesPromise) {
        this.wppCloudSendersLoading = true;
        try {
          await sharedWppTemplatesPromise;
        } finally {
          this.wppCloudTemplates = sharedWppTemplatesCache;
          this.wppCloudSendersLoading = false;
        }
        return;
      }

      this.wppCloudSendersLoading = true;
      sharedWppTemplatesPromise = (async () => {
        try {
          const res = await this.$http(keviApi.getOptions("/senders", "GET"));
          const wppSenders = (res.body?.my_senders || []).filter((s) => s.channel === "whatsapp-cloud");
          await Promise.all(wppSenders.map(async (sender) => {
            const tRes = await this.$http(keviApi.getOptions(`senders/${sender.id}/templates`, "GET"));
            this.$set(sharedWppTemplatesCache, sender.id, tRes.body || []);
          }));
        } catch (e) {
          sharedWppTemplatesPromise = null;
        }
      })();

      await sharedWppTemplatesPromise;
      this.wppCloudTemplates = sharedWppTemplatesCache;
      this.wppCloudSendersLoading = false;
    },

    getWppCloudTemplate(senderId, templateId) {
      if (Array.isArray(this.templates)) {
        const found = this.templates.find(t => String(t.id) === String(templateId));
        if (found) return found;
      }

      if (this.templates && typeof this.templates === 'object' && !Array.isArray(this.templates)) {
        const list = this.templates[senderId] || [];
        const found = list.find(t => String(t.id) === String(templateId));
        if (found) return found;
      }

      return (this.wppCloudTemplates[senderId] || []).find((t) => String(t.id) === String(templateId));
    },
  }
}
</script>
<style lang="less">
.kv-message-wrapper {
  width: 100%;
  border-radius: 16px;
  overflow: auto;

  .kv-message-inner {
    width: 70%;
    margin: 16px 0 16px 7%;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-height: 40px;
  }

  .kv-time {
    font-size: 12px;
    font-weight: 500;
    color: #6C737F;
    line-height: 1;
    white-space: nowrap;
  }

  .kv-message-renderer {
    font-size: 14.2px;
    color: #111827;
    line-height: 1.5;
    word-break: break-word;

    .kv-content-url {
      color: #004eeb;
      text-decoration: none;
      word-break: break-all;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  // --- WHATSAPP STYLES ---
  &.kv-message-whatsapp {
    background-image: url(/images/whatsapp-bg.png);
    background-color: #E5DDD5;
    background-size: cover;

    .wpp-media-full {
      margin: -6px -7px 6px -9px; // Remove padding do container pai
      border-bottom: 1px solid #f0f0f0;
      overflow: hidden;
      background: black; // Fundo preto para vídeos carregando
      display: flex;
      justify-content: center;

      img,
      video {
        width: 100%;
        height: auto;
        display: block;
        object-fit: cover;
        max-height: 300px; // Limite de altura
      }

      video {
        max-height: 250px; // Vídeos geralmente são um pouco menores
      }
    }

    // Estilo específico para o Card de Documento
    .wpp-media-doc {
      margin-bottom: 8px;

      .wpp-doc-card {
        display: flex;
        align-items: center;
        background-color: rgba(0, 0, 0, 0.05); // Fundo cinza suave
        border-radius: 6px;
        padding: 10px;
        text-decoration: none;
        color: #333;
        transition: background 0.2s;
        gap: 10px;
        border: 1px solid rgba(0, 0, 0, 0.04);

        &:hover {
          background-color: rgba(0, 0, 0, 0.09);
        }
      }

      .wpp-doc-icon {
        font-size: 24px;
        color: #ff5252; // Cor de ícone PDF/Doc (vermelho/genérico)
        display: flex;
        align-items: center;
      }

      .wpp-doc-name {
        font-size: 13px;
        font-weight: 500;
        color: #111;
        flex-grow: 1; // Empurra o ícone de download para o final
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis; // "..." se o nome for longo
        max-width: 180px;
      }

      .wpp-doc-dl {
        color: #999;
        font-size: 12px;
      }
    }

    // Mantém o estilo do placeholder antigo (caso não tenha URL)
    .wpp-media-placeholder {
      background: #f0f2f5;
      height: 120px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #54656f;
      font-weight: 500;
      border-bottom: 1px solid #f0f0f0;
      margin: -6px -7px 6px -9px;
    }

    .kv-skeleton-wrapper {
      padding: 12px 14px;
    }

    .kv-message-renderer {
      background-color: #FFFFFF;
      border-radius: 7.5px;
      box-shadow: 0 1px 0.5px rgba(0, 0, 0, 0.13);
      overflow: hidden;
      position: relative;
      display: flex;
      flex-direction: column;
    }

    .wpp-content-wrapper {
      padding: 6px 7px 8px 9px;
      position: relative;
      display: flow-root;
      width: 100%;
      box-sizing: border-box;
    }

    .wpp-header {
      font-weight: 600;
      padding-bottom: 4px;
      font-size: 15px;
      color: #111927;
    }

    .wpp-body {
      padding-bottom: 0px;
      white-space: pre-wrap;
      color: #111b21;
      position: relative;
      overflow-wrap: break-word;
    }

    // Estilos do Missing Template
    .wpp-missing-body {
      font-style: italic;
      color: #54656f;
      display: flex;
      flex-direction: column;
      gap: 2px;
      padding-bottom: 4px;

      i {
        color: #eab308;
        margin-right: 4px;
      }
    }

    .wpp-missing-id {
      font-size: 10px;
      opacity: 0.7;
      font-family: monospace;
      margin-left: 2px;
    }

    .wpp-footer {
      font-size: 12px;
      color: #6C737F;
      margin-top: 4px;
      margin-bottom: 2px;
      display: block;
      font-weight: 500;
    }

    .wpp-embedded-time {
      float: right;
      font-size: 12px;
      color: #6C737F;
      font-weight: 500;
      margin-left: 10px;
      margin-top: 8px;
      margin-bottom: -4px;
      line-height: 1;
      position: relative;
      top: 2px;
      height: 15px;
      display: inline-block;
      pointer-events: none;
      white-space: nowrap;
    }

    .wpp-media {
      background: #f0f2f5;
      height: 120px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #54656f;
      font-weight: 500;
      border-bottom: 1px solid #f0f0f0;
      margin: -6px -7px 6px -9px;
    }

    .wpp-buttons-container {
      border-top: 0.5px solid #E5E7EB;
      background-color: #fff;
      display: flex;
      flex-direction: column;
      position: relative;
      z-index: 2;
      margin-top: auto;
    }

    .wpp-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 10px;
      color: #4F9D76;
      font-weight: 500;
      text-decoration: none;
      border-bottom: 1px solid #e9edef;
      font-size: 13px;
      cursor: pointer;
      transition: background 0.2s;
      gap: 8px;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background-color: #f5f6f6;
      }
    }
  }

  // --- SMS STYLES ---
  &.kv-message-sms {
    background-color: #FFFFFF;
    border: 1px solid #E5E7EB;

    .kv-skeleton-wrapper {
      padding: 16px;
    }

    .kv-message-inner {
      align-items: center;
    }

    .kv-time {
      text-align: center;
      margin-bottom: 4px;
    }

    .kv-message-renderer {
      background-color: #E5E7EB;
      padding: 16px;
      border-radius: 12px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

      img {
        max-width: 100%;
        border-radius: 8px;
      }
    }
  }

  &.kv-message-rcs {
    background: linear-gradient(180deg, #f4f7fb 0%, #e7eef6 100%);
    border: 1px solid #d7e0ea;

    .kv-message-inner {
      align-items: flex-start;
      width: 88%;
    }

    .kv-time {
      margin-top: 2px;
    }

    .kv-message-renderer.kv-rcs {
      background: transparent;
      padding: 0;
      box-shadow: none;
      width: 100%;
    }

    .kv-rcs-bubble {
      background: #fff;
      color: #111827;
      border-radius: 4px 18px 18px 18px;
      padding: 12px 14px;
      box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
      white-space: pre-wrap;
      max-width: 100%;
    }

    .kv-rcs-carousel {
      display: flex;
      gap: 10px;
      width: 100%;
      overflow-x: auto;
      padding: 2px 2px 8px;
      scroll-snap-type: x mandatory;
    }

    .kv-rcs-card {
      display: flex;
      flex-direction: column;
      width: 240px;
      flex: 0 0 240px;
      background: #fff;
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(16, 24, 40, 0.12);
      scroll-snap-align: start;

      &.is-single {
        width: ~"min(100%, 320px)";
        flex-basis: ~"min(100%, 320px)";
      }
    }

    .kv-rcs-media {
      background: #dbe4ee;
      aspect-ratio: 16 / 10;
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        border-radius: 0;
      }

      &.is-empty {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 120px;
        color: #6c737f;
        font-size: 22px;
      }
    }

    .kv-rcs-card-body {
      padding: 12px 14px 10px;
    }

    .kv-rcs-title {
      font-size: 15px;
      font-weight: 600;
      color: #111827;
      margin-bottom: 4px;
    }

    .kv-rcs-text {
      font-size: 13.5px;
      color: #384250;
      white-space: pre-wrap;
    }

    .kv-rcs-actions {
      margin-top: auto;
      border-top: 1px solid #e8edf3;
      display: flex;
      flex-direction: column;
    }

    .kv-rcs-action {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 10px 12px;
      color: #0b57d0;
      font-weight: 600;
      font-size: 13px;
      text-decoration: none;
      border-bottom: 1px solid #eef2f6;
      min-width: 0;

      span {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background: #f5f8fc;
      }
    }

    .kv-rcs-file {
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 220px;
      max-width: 100%;
      background: #fff;
      border-radius: 16px;
      padding: 12px 14px;
      text-decoration: none;
      color: inherit;
      box-shadow: 0 1px 3px rgba(16, 24, 40, 0.12);

      &:hover {
        background: #f8fafc;
      }
    }

    .kv-rcs-file-icon {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      background: #e8f0fe;
      color: #0b57d0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      flex: 0 0 40px;
    }

    .kv-rcs-file-meta {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }

    .kv-rcs-file-name {
      font-weight: 600;
      color: #111827;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .kv-rcs-file-kind {
      font-size: 12px;
      color: #6c737f;
    }
  }
}
</style>
